/**
 * Замечания к «Юниверситету» (16.10.2026): долгое нажатие по любому месту вкладки. Вешается на корень вкладки —
 * React-события из шторок-порталов всплывают туда же. Правила:
 *  • держим палец 550 мс почти на месте (сдвиг больше 10 px — это прокрутка, жест отменён) → элемент подсвечен,
 *    лёгкая вибрация; отпустили — открывается окно замечания. После подсветки сдвиг жест уже не отменяет (ревью
 *    16.10: мышь увела на 15 px — клик ушёл бы в вариант ответа); прокрутку пальцем браузер сообщит pointercancel;
 *  • «хвостовой» клик после удержания глотаем: долгое нажатие на вариант ответа НЕ отвечает на вопрос;
 *  • Android на долгое нажатие шлёт contextmenu — его меню гасим, а сам жест засчитываем сразу;
 *  • мышь: правая кнопка — сразу замечание (Telegram Desktop);
 *  • поля ввода не трогаем (там своё долгое нажатие — вставка), второй палец (щипок) — отмена;
 *  • прокрутка после удержания (pointercancel) — не замечание.
 */
import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent } from "react";
import { tgHaptic } from "../../../../lib/telegram";

export const HOLD_MS = 550;
const SLOP_PX = 10;
const SKIP = "input, textarea, select, [contenteditable=''], [contenteditable='true'], [data-fb-skip]";

type Press = { id: number; x: number; y: number; el: Element; timer: number; armed: boolean };

/** Глотаем ровно один клик после жеста (он приходит сразу за pointerup); не пришёл — снимаем ловушку. */
function swallowNextClick(): void {
  const stop = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); done(); };
  const done = () => { window.removeEventListener("click", stop, true); window.clearTimeout(t); };
  window.addEventListener("click", stop, true);
  const t = window.setTimeout(done, 700);
}

export function useHoldPress(onHold: (el: Element) => void, enabled: boolean) {
  const press = useRef<Press | null>(null);
  const lastType = useRef("");
  /* жест засчитан, а палец ещё на экране (Android contextmenu): клик проглотим, когда отпустит */
  const upPending = useRef<number | null>(null);
  const on = useRef(enabled);
  on.current = enabled;
  const hold = useRef(onHold);
  hold.current = onHold;

  const reset = useCallback((keepMark = false) => {
    const p = press.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    if (!keepMark) p.el.removeAttribute("data-fb-on");
    press.current = null;
  }, []);
  useEffect(() => () => reset(), [reset]);
  useEffect(() => { if (!enabled) reset(); }, [enabled, reset]);

  const fire = useCallback((el: Element, downId: number | null) => {
    el.setAttribute("data-fb-on", "");
    reset(true);
    if (downId == null) swallowNextClick(); else upPending.current = downId;
    hold.current(el);
  }, [reset]);

  const onPointerDown = useCallback((e: PointerEvent) => {
    lastType.current = e.pointerType;
    if (!e.isPrimary) { reset(); return; }                       // второй палец — щипок, не замечание
    if (!on.current || (e.pointerType === "mouse" && e.button !== 0)) return;
    const el = e.target instanceof Element ? e.target : null;
    if (!el || el.closest(SKIP)) return;
    reset();
    const p: Press = { id: e.pointerId, x: e.clientX, y: e.clientY, el, armed: false, timer: 0 };
    p.timer = window.setTimeout(() => {
      if (press.current !== p) return;
      p.armed = true;
      el.setAttribute("data-fb-on", "");
      tgHaptic("tap");
    }, HOLD_MS);
    press.current = p;
  }, [reset]);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const p = press.current;
    if (p && !p.armed && e.pointerId === p.id && Math.hypot(e.clientX - p.x, e.clientY - p.y) > SLOP_PX) reset();
  }, [reset]);

  const onPointerUp = useCallback((e: PointerEvent) => {
    if (upPending.current === e.pointerId) { upPending.current = null; swallowNextClick(); return; }
    const p = press.current;
    if (!p || e.pointerId !== p.id) return;
    if (p.armed && on.current) fire(p.el, null); else reset();
  }, [fire, reset]);

  const onPointerCancel = useCallback((e: PointerEvent) => {
    if (upPending.current === e.pointerId) upPending.current = null;
    reset();
  }, [reset]);

  const onContextMenu = useCallback((e: MouseEvent) => {
    const el = e.target instanceof Element ? e.target : null;
    if (!el || el.closest(SKIP)) return;                           // в поле ответа — системное меню (вставить)
    e.preventDefault();                                            // Android: меню «копировать/выделить» рвёт жест
    if (!on.current) return;
    const p = press.current;
    if (lastType.current === "mouse") { reset(); fire(el, null); } // правая кнопка мыши (клика за ней нет)
    else if (p) fire(p.el, p.id);                                  // Android: система засчитала долгое нажатие, палец ещё внизу
  }, [fire, reset]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onContextMenu };
}
